import { Button } from '@/shared/ui/button';
import { LogOut, Home, FileText, Bug, Building2, Lock, Globe, Book, Check, Shield, Headset } from 'lucide-react';
import { useUser } from '@/contexts/UserContext';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/shared/ui/dropdown-menu";
import orbiusLogo from '@/assets/orbius_logo.png';
import { useNavigate } from 'react-router-dom';
import { ROUTES } from '@/config/routeConfig';
import DebugLogger from '@/shared/components/DebugLogger';
import { useEffect, useState } from 'react';
import GenericAlertDialog from '@/shared/components/GenericAlertDialog';
import { envConfig, Environment } from "@/shared/services/apiConfig";
import { AccountDetailsTab } from '@/shared/config/account-details-tabs';
import ChangePasswordDialog from '@/domains/users/dialogs/ChangePasswordDialog';
import MfaSettingsDialog from '@/app/auth/dialogs/MfaSettingsDialog';
import SupportTicketDialog from '@/shared/components/SupportTicketDialog';
import { Organization } from '@/shared/types/userTypes';
import { useRouteNavigation } from '@/shared/hooks/use-route-navigation';
import { ThemeToggle } from '@/shared/components/ThemeToggle';
import { getRoleName, getShortOrgRoleName } from '@/shared/types/userRoleTypes';
import { useUserRoles } from '@/shared/hooks/userRoles';
import { Badge } from '@/shared/ui/badge';
import { useOrganizationContextSwitch } from '../hooks/use-account-context-switch';

const PASSWORD_CHANGE_PROMPT_SHOWN_KEY = 'passwordChangePromptShown';

function getInitials(name: string | undefined): string {
  if (!name?.trim()) return '?';
  const parts = name.trim().split(/\s+/);
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
}

const TopBar = ({ shouldAllowRegionChange, shouldAllowAccountChange }: { shouldAllowRegionChange: boolean, shouldAllowAccountChange: boolean }) => {
  const navigate = useNavigate();
  const [isDebugLoggerOpen, setIsDebugLoggerOpen] = useState(false);
  const [isLogoutDialogOpen, setIsLogoutDialogOpen] = useState(false);
  const [isSupportTicketDialogOpen, setIsSupportTicketDialogOpen] = useState(false);
  const {
    currentUser,
    selectedOrganization,
    selectedParentOrganization,
    selectedRegionCode,
    orgRole,
    handleLogout,
    handleRegionChange,
  } = useUser();
  const { isPlatformUser, isTenantUser, isCustomerUser } = useUserRoles();
  const { switchOrganizationAndNavigateToAccountDetails, switchOrganizationAndNavigateToCustomerDetails } = useRouteNavigation();
  const { isSwitchingOrganizationContext } = useOrganizationContextSwitch();
  const [isChangePasswordDialogOpen, setIsChangePasswordDialogOpen] = useState(false);
  const [isMfaSettingsDialogOpen, setIsMfaSettingsDialogOpen] = useState(false);

  // Auto-prompt once per login session when a password change is required.
  useEffect(() => {
    if (currentUser?.passwordChangeRequired !== true) return;
    if (sessionStorage.getItem(PASSWORD_CHANGE_PROMPT_SHOWN_KEY) === 'true') return;

    sessionStorage.setItem(PASSWORD_CHANGE_PROMPT_SHOWN_KEY, 'true');
    setIsChangePasswordDialogOpen(true);
  }, [currentUser?.userId, currentUser?.passwordChangeRequired]);

  // Handle region change with refetch
  const handleRegionChangeWithRefetch = (regionCode: string) => {
    handleRegionChange(regionCode);
  };

  const handleOrganizationChange = async (organization: Organization) => {
    switchOrganizationAndNavigateToAccountDetails({ organization, accountDetailsTab: AccountDetailsTab.DASHBOARD, replace: true });
  };

  const handleCustomerOrganizationChange = async (organization: Organization, parentOrganization: Organization) => {
    switchOrganizationAndNavigateToCustomerDetails(organization, parentOrganization, true);
  };


  const handleLogoutAction = () => {
    setIsLogoutDialogOpen(true);
  }

  const handleLogoutConfirm = () => {
    handleLogout();
    setIsLogoutDialogOpen(false);
  }

  const handleLogoAction = () => {
    if (isPlatformUser) {
      navigate(ROUTES.ACCOUNTS, { replace: true });
    } else {
      handleHomeAction();
    }
  }

  const handleHomeAction = () => {
    if (isPlatformUser) {
      navigate(ROUTES.ACCOUNTS, { replace: true });
    } else if (selectedOrganization && isTenantUser) {
      handleOrganizationChange(selectedOrganization);
    } else if (selectedOrganization && selectedParentOrganization && isCustomerUser) {
      handleCustomerOrganizationChange(selectedOrganization, selectedParentOrganization);
    }
  }

  return (
    <>
      <div className="fixed left-0 right-0 top-0 z-20 flex h-20 flex-row items-center justify-between border-b border-border/50 glass-panel">
        {/* Left section with logo */}
        <div className="flex items-center w-80 h-full">
          <Button
            variant="ghost"
            size="sm"
            className="justify-start w-auto h-full"
            onClick={handleLogoAction}
          >
            <img src={orbiusLogo} alt="Orbius Logo" className="h-12 w-auto dark:invert" />
          </Button>
        </div>

        {/* Center section with Home, SLA, and Help Center */}
        <div className="flex">
          <Button
            variant="ghost"
            size="sm"
            className="gap-2"
            onClick={handleHomeAction}
          >
            <Home className="h-4 w-4" />
            <span className={`${(window.location?.pathname ?? '').includes('home') ? 'underline underline-offset-4 decoration-2' : ''}`}>Home</span>
          </Button>
          {/* Show SLA and Help Center for non-account customers */}
          {!isCustomerUser && (
            <>
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                onClick={() => navigate(ROUTES.SLA, { replace: true })}
              >
                <FileText className="h-4 w-4" />
                <span className={`${(window.location?.pathname ?? '').includes(ROUTES.SLA) ? 'underline underline-offset-4 decoration-2' : ''}`}>SLA</span>
              </Button>
              {isPlatformUser && (
                <Button
                  variant="ghost"
                  size="sm"
                  className="gap-2"
                  onClick={() => navigate(ROUTES.USER_GUIDE, { replace: true })}
                >
                  <Book className="h-4 w-4" />
                  <span className={`${(window.location?.pathname ?? '').includes(ROUTES.USER_GUIDE) ? 'underline underline-offset-4 decoration-2' : ''}`}>User Guide</span>
                </Button>
              )}
              <Button
                variant="ghost"
                size="sm"
                className="gap-2"
                onClick={() => setIsSupportTicketDialogOpen(true)}
              >
                <Headset className="h-4 w-4" />
                <span>Support</span>
              </Button>
            </>
          )}
        </div>

        {/* Right section: initials trigger → dropdown with name/email, region or organization, logout */}
        <div className="flex flex-row items-center justify-end gap-2 pr-4 w-80 h-full">
          {isPlatformUser && <ThemeToggle />}
          {envConfig.appEnv === Environment.DEVELOPMENT && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsDebugLoggerOpen(true)}
              className="gap-2"
            >
              <Bug className="h-4 w-4" />
            </Button>
          )}
          {currentUser && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  size="icon"
                  className="h-10 w-10 rounded-full bg-primary/10 text-primary font-medium hover:bg-primary/20"
                >
                  {getInitials(currentUser.name)}
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-80">
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-row items-center gap-2 h-10 items-start justify-between">
                    <div className="flex flex-col gap-0.5">
                      <p className="text-sm font-medium">{currentUser.name}</p>
                      <p className="text-xs">{currentUser.email}</p>
                    </div>
                    {isPlatformUser && (
                      <Badge variant="default" className="text-xs">{getShortOrgRoleName(orgRole)}</Badge>
                    )}
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                {/* Region selector for SUPER_ADMIN */}
                {isPlatformUser && shouldAllowRegionChange && currentUser.availableRegions && currentUser.availableRegions.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-sm font-medium py-1">
                      {currentUser.availableRegions.length > 1 ? 'Datapools' : 'Datapool'}
                    </DropdownMenuLabel>
                    {currentUser.availableRegions.map((region) => (
                      <DropdownMenuItem
                        key={region.regionCode}
                        onClick={() => handleRegionChangeWithRefetch(region.regionCode)}
                        className={selectedRegionCode === region.regionCode ? "bg-primary/10" : ""}
                      >
                        <div className='flex flex-row items-center justify-start gap-2'>
                          <Globe className="h-4 w-4" />
                          <div className="flex flex-col items-start justify-start">
                            <span className="text-sm text-left font-medium">{region.name}</span>
                            <span className="text-xs">{region.regionCode}</span>
                          </div>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </>
                )}
                {/* Account → organization selector for tenant/customer users */}
                {shouldAllowAccountChange && currentUser.organizations && currentUser.organizations.length > 0 && (
                  <>
                    <DropdownMenuLabel className="text-sm font-medium py-1">
                      {currentUser.organizations.length > 1 ? 'Accounts' : 'Account'}
                    </DropdownMenuLabel>
                    {currentUser.organizations.map((organization) => {
                      const parentOrganizations = organization.parentOrganizations ?? [];
                      const organizationActive = selectedOrganization?.organizationId === organization.organizationId;

                      return (
                        <DropdownMenuGroup key={organization.organizationId}>
                          <DropdownMenuItem
                            key={organization.organizationId}
                            onClick={() => parentOrganizations.length == 0 ? handleOrganizationChange(organization) : null}
                            disabled={isSwitchingOrganizationContext}
                            className={`${organizationActive ? "bg-primary/10" : ""} gap-2 p-4`}
                          >
                            <div className="flex flex-row items-center justify-between w-full">
                              <div className="flex flex-row items-center justify-start gap-2">
                                <Building2 className="h-4 w-4" />
                                <span className="text-sm font-medium">{organization.name}</span>
                              </div>
                              {organization.role && <Badge variant="default" className="text-xs">{getRoleName(organization.role)}</Badge>}
                            </div>
                          </DropdownMenuItem>
                          {parentOrganizations.map((parentOrganization) => {
                            const parentOrganizationActive = organizationActive && selectedParentOrganization?.organizationId === parentOrganization.organizationId;
                            return (
                              <DropdownMenuItem
                                key={`${organization.organizationId}-${parentOrganization.organizationId}`}
                                onClick={() => handleCustomerOrganizationChange(organization, parentOrganization)}
                                disabled={isSwitchingOrganizationContext}
                                className={`${parentOrganizationActive ? "bg-primary/20" : ""} gap-2 p-4`}
                              >
                                <div className="flex flex-row items-center justify-between w-full">
                                  <div className="flex flex-row items-center justify-start gap-2">
                                    {parentOrganizationActive && <Check className="h-4 w-4" />}
                                    <span className="text-sm font-medium">{parentOrganization.name}</span>
                                  </div>
                                  {parentOrganization.role && <Badge variant="default" className="text-xs">{getRoleName(parentOrganization.role)}</Badge>}
                                </div>
                              </DropdownMenuItem>
                            )
                          })}
                        </DropdownMenuGroup>
                      );
                    })}
                  </>
                )}
                <DropdownMenuSeparator />
                {/* Change Password */}
                <DropdownMenuItem onClick={() => setIsChangePasswordDialogOpen(true)} className="text-primary focus:text-primary">
                  <div className="flex flex-row items-center justify-start gap-2 h-10 text-blue-500">
                    <Lock className="h-4 w-4" />
                    <span className="text-sm font-medium">Change Password</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => setIsMfaSettingsDialogOpen(true)} className="text-primary focus:text-primary">
                  <div className="flex flex-row items-center justify-start gap-2 h-10 text-blue-500">
                    <Shield className="h-4 w-4" />
                    <span className="text-sm font-medium">Security</span>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                {/* Logout */}
                <DropdownMenuItem onClick={(e) => {
                  e.stopPropagation();
                  handleLogoutAction();
                }}>
                  <Button variant="ghost" size="sm" className="gap-2 w-full text-destructive hover:text-destructive">
                    <LogOut className="h-4 w-4" />
                    Logout
                  </Button>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div >

      {/* Debug Logger Modal */}
      {
        isDebugLoggerOpen && (
          <DebugLogger
            isOpen={isDebugLoggerOpen}
            onClose={() => setIsDebugLoggerOpen(false)}
          />
        )
      }

      {/* Logout Confirmation Dialog */}
      <GenericAlertDialog
        open={isLogoutDialogOpen}
        onOpenChange={setIsLogoutDialogOpen}
        title="Logout"
        description="Are you sure you want to logout?"
        confirmLabel="Yes, Logout"
        cancelLabel="Cancel"
        onConfirm={handleLogoutConfirm}
      />

      {/* Change Password Dialog */}
      {currentUser && (
        <ChangePasswordDialog
          isOpen={isChangePasswordDialogOpen}
          onClose={() => setIsChangePasswordDialogOpen(false)}
          user={currentUser}
        />
      )}
      {currentUser && (
        <MfaSettingsDialog
          isOpen={isMfaSettingsDialogOpen}
          onClose={() => setIsMfaSettingsDialogOpen(false)}
        />
      )}

      <SupportTicketDialog
        isOpen={isSupportTicketDialogOpen}
        onClose={() => setIsSupportTicketDialogOpen(false)}
        defaultCompany={selectedParentOrganization?.name ?? selectedOrganization?.name ?? ''}
      />
    </>
  );
};

export default TopBar;